// src/components/common/Avatar/ProfileAvatar.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  Building,
  User,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  BookOpen,
  CalendarClock,
  UserCheck,
  History,
  HelpCircle,
  BarChart3,
  CheckCircle,
} from "lucide-react";
import logo from '../../../Images/logo.png';
import "../../../styles/ProfileAvatar.css"
import MyProfile from "../../../pages/MyProfile";
import EmployeeForm from "../../employees/EmployeeForm";
import CourseCreationForm from "../../courses/CourseCreationForm";
import CourseDetailsView from "../../courses/CourseDetailsView";
import { fetchUserData } from "../../../services/authServicepa";
import { checkPermissions } from "../../../utils/permissions";

const ProfileAvatarpp = () => {
  const [email, setEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const navigate = useNavigate();

  // Fetch user email, role and available branches/locations from API
  useEffect(() => {
    const getUserData = async () => {
      try {
        const userData = await fetchUserData();
        if (userData) {
          setEmail(userData.email_id || "");
          setUserRole(userData.role || "");
          setBranches(userData.branch || []);
          setLocations(userData.location || []);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    getUserData();
  }, [navigate]);
const loggedInUserId = localStorage.getItem("emp_id"); // or whatever your user id key is

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("emp_name");
    localStorage.removeItem("placementFilterState");
    localStorage.removeItem("placementDbFilters");
    localStorage.removeItem("placedStudentFilters");
    localStorage.removeItem("jobDashboardFilters");
    localStorage.removeItem("jobDashboard-selectedColumns");
    sessionStorage.removeItem("lastActiveTab");
    sessionStorage.removeItem("lastJobReqPath");
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith(`feedback_given_${loggedInUserId}_`)) {
        sessionStorage.removeItem(key);
      }
    });
    localStorage.clear();
    setEmail("");
    navigate("/", { replace: true });
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Add this function to handle My Profile click
  const handleMyProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMyProfile(true);
  };

  const handleMouseMove = (e) => {
    if (isCollapsed && tooltip.show) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX + 15,
        y: e.clientY - 10
      }));
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setTooltip({ show: false, text: '', x: 0, y: 0 });
    }
  };

  const navItems = [
    // Main Navigation
    ...(["Super-Admin", "Branch-head"].includes(userRole) ? [{
      label: "Landing Page",
      icon: Home,
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log("Navigating to /landingpage");
        navigate("/landingpage");
        console.log("Navigation called");
      }
    }] : []),

    ...(["Placement officer", "Branch-head", "Super-Admin"].includes(userRole) ? [{
      label: "Home Page",
      icon: Home,
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/placementdb");
      }
    }] : []),

    // ...(["Placement officer", "Branch-head", "Super-Admin"].includes(userRole) ? [{
    //   label: "Passed Placement Drives",
    //   icon: CheckCircle,
    //   action: (e) => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     navigate("/placementpassed");
    //   }
    // }] : []),
    ...(["Trainer"].includes(userRole) ? [{
      label: "Home Page",
      icon: Home,
      action: () => navigate("/staff-dashboard")
    }] : []),
    ...(["Placement officer", "Branch-head", "Super-Admin", "Trainer"].includes(userRole) ? [{
      label: "Placed Student",
      icon: UserCheck,
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/placedready");
      }
    }] : []),


    ...(["Trainer"].includes(userRole) ? [{
      label: "Mentor Dashboard",
      icon: History, // 🆕 Use the imported icon
      action: () => navigate("/old-student-dashboard")
    }] : []),

    ...(["Trainer"].includes(userRole) ? [{
      label: "Student Entry Form",
      icon: BookOpen, // 👈 good icon for adding a new student
      action: () => navigate("/students-form")
    }] : []),

    ...(["Super-Admin", "Branch-head", "Placement officer", "Trainer"].includes(userRole) ? [
      {
        label: "Create Job Requirements",
        icon: Building,
        action: (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate("/placementofficer", { replace: true });
        }
      },
      {
        label: "Job Dashboard",
        icon: CalendarClock,
        action: (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate("/job-dashboard");
        }
      },

    ] : []),
    ...(["Placement officer", "Branch-head", "Super-Admin"].includes(userRole)
  ? [
      {
        label: "Reports",
        icon: BarChart3, // or any icon you are using
        action: (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate("/reports");
        }
      }
    ]
  : []),

    // Employee Management
    ...(checkPermissions.canCreateEmployee(userRole) ? [{
      label: "Create Employee",
      icon: UserPlus,
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowEmployeeForm(true);
      }
    }] : []),

    ...(checkPermissions.canViewEmployees(userRole) ? [{
      label: "View Employees",
      icon: Users,
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/employees");
      }
    }] : []),
  ];
  

  const settingsItems = [
    ...(["Super-Admin"].includes(userRole) ? [{
      label: "Reset to Default Password",
      icon: Settings,
      action: () => navigate("/defaultpassword")
    }] : []),
    {
      label: "Support",
      icon: HelpCircle, // You can change icon to something like HelpCircle if you want
      action: () => setShowSupportPopup(true)
    },
    {
      label: "Logout",
      icon: LogOut,
      action: handleLogout
    }
  ];

  return (
    <>
      <div
        className={`avatar-container ${isCollapsed ? 'collapsed' : ''}`}
        onMouseEnter={() => setIsCollapsed(false)}   // expand on hover
        onMouseLeave={() => setIsCollapsed(true)}    // collapse when leaving
      >
        <div className="avatar-profile-container">
          <div className="avatar-button">
            <img src={logo} alt="PUMO TECHNOVATION" className="logo-image" />
          </div>

          <div className="avatar-emp-name">PUMO TECHNOVATION</div>
          <button
            className="avatar-dropdown-trigger"
            onClick={toggleSidebar} // keep toggle as backup
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <div className="avatar-dropdown">
          <div className="dropdown-email">{email || "Fetching..."}</div>

          {/* Main Navigation Section */}
          <div className="nav-section">
            <div className="nav-section-title">Main</div>
            {navItems.map((item, index) => {
              return (
                <button
                  key={index}
                  className="dropdown-item"
                  onClick={item.action}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Conditionally render image or icon */}
                  {item.icon === "image" ? (
                    <img
                      src={item.imageSrc}
                      alt={item.label}
                      className="navsb-image"
                      style={{
                        width: '20px',
                        height: '20px',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    React.createElement(item.icon, { size: 20, className: "navsb-icon" })
                  )}
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Profile Section */}
          <div className="nav-section">
            <div className="nav-section-title">Profile</div>
            <button
              className="dropdown-item"
              onClick={handleMyProfileClick} // Add onClick handler
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <User size={20} className="navsb-icon" />
              <span className="nav-label">My Profile</span> {/* Just text, not component */}
            </button>
          </div>

          {/* Settings Section */}
          <div className="nav-section">
            <div className="nav-section-title">Settings</div>
            {settingsItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={index}
                  className="dropdown-item"
                  onClick={item.action}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <IconComponent size={20} className="navsb-icon" />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cursor Following Tooltip */}
      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: '#1e293b',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none',
            transform: 'translateY(-50%)'
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Modal Forms */}
      {showEmployeeForm && (
        <EmployeeForm
          branches={branches}
          locations={locations}
          userRole={userRole}
          onClose={() => setShowEmployeeForm(false)}
        />
      )}

      {showCourseForm && (
        <CourseCreationForm
          onClose={() => setShowCourseForm(false)}
        />
      )}

      {showCourseDetails && (
        <CourseDetailsView
          onClose={() => setShowCourseDetails(false)}
          onDelete={(courseId) => console.log('Course deleted:', courseId)}
        />
      )}
      {showSupportPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h3>Support</h3>
            <p>If you have any queries related to this application, contact Mr. Hari -<strong>9025680801</strong></p>
            <p>For any escalations, contact <strong>Mr.Muthu – 9176655299</strong></p>
            <button className="closes-btn" onClick={() => setShowSupportPopup(false)}>
              Close
            </button>
          </div>
        </div>
      )}


      {/* My Profile Modal */}
      {showMyProfile && (
        <MyProfile onClose={() => setShowMyProfile(false)} />
      )}
    </>
  );
};

export default ProfileAvatarpp;